import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AvailabilityLegend } from '../components/availability/AvailabilityLegend';
import {
  STATE_STYLES, SHIFT_STATUS_STYLES, SUB_STATUS_STYLES, SHIFT_META, fmtShortDate,
} from '../components/availability/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AvailabilityHeatmap() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [data, setData] = useState(null);
  const [shiftMode, setShiftMode] = useState('overall');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [weekendOnly, setWeekendOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/availability/periods`).then((res) => {
      setPeriods(res.data);
      setPeriodId(res.data[0]?.id || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const fetchHeatmap = useCallback(async () => {
    if (!periodId) return;
    const res = await axios.get(`${API_URL}/api/availability/periods/${periodId}/heatmap`);
    setData(res.data);
  }, [periodId]);

  useEffect(() => { fetchHeatmap(); }, [fetchHeatmap]);

  const cellState = (cell) => {
    if (shiftMode === 'overall') return cell.state;
    const s = cell.shifts?.[shiftMode];
    if (cell.unavailable_all_day) return 'unavailable';
    if (!s) return 'no_response';
    return s === 'preferred' ? 'preferred' : s === 'available' ? 'available' : 'unavailable';
  };

  const isWeekend = (d) => {
    const wd = new Date(d + 'T00:00:00').getDay();
    return wd === 0 || wd === 6;
  };

  const rows = (data?.rows || []).filter((r) => {
    if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (weekendOnly) {
      const ok = (data?.dates || []).some((d) => isWeekend(d) && ['preferred', 'available', 'limited'].includes(cellState(r.cells[d])));
      if (!ok) return false;
    }
    if (stateFilter !== 'all') {
      const ok = (data?.dates || []).some((d) => cellState(r.cells[d]) === stateFilter);
      if (!ok) return false;
    }
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="availability-heatmap-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Availability Heatmap</h1>
          <p className="text-sm text-muted-foreground mt-1">Who can work when — at a glance.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/availability-admin')} data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Availability Management
        </Button>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={periodId || ''} onValueChange={setPeriodId}>
            <SelectTrigger className="w-full sm:w-[260px] bg-background" data-testid="heatmap-period-select"><SelectValue placeholder="Period" /></SelectTrigger>
            <SelectContent className="bg-background">
              {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={shiftMode} onValueChange={setShiftMode}>
            <SelectTrigger className="w-[160px] bg-background" data-testid="heatmap-shift-mode"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-background">
              <SelectItem value="overall">Overall day</SelectItem>
              {SHIFT_META.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} shift only</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-[170px] bg-background" data-testid="heatmap-state-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-background">
              <SelectItem value="all">Any availability</SelectItem>
              <SelectItem value="preferred">Has Preferred</SelectItem>
              <SelectItem value="available">Has Available</SelectItem>
              <SelectItem value="unavailable">Has Unavailable</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-background" data-testid="heatmap-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-background">
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="past_due">Past due</SelectItem>
              <SelectItem value="not_started">Not started</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Employee…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-[150px]" data-testid="heatmap-search" />
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={weekendOnly} onChange={(e) => setWeekendOnly(e.target.checked)} className="rounded" data-testid="heatmap-weekend-toggle" />
            Weekend availability
          </label>
        </div>
      </Card>

      <AvailabilityLegend />

      {data && (
        <Card className="p-3 overflow-x-auto" data-testid="heatmap-grid">
          <table className="border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-card text-left text-xs font-semibold text-muted-foreground pr-3 pb-2 min-w-[130px] z-10">Employee</th>
                {data.dates.map((d) => {
                  const dt = new Date(d + 'T00:00:00');
                  const weekend = isWeekend(d);
                  return (
                    <th key={d} className={`text-[9px] font-semibold px-0.5 pb-2 min-w-[30px] ${weekend ? 'text-[#1D9E75]' : 'text-muted-foreground'}`}>
                      <div>{dt.toLocaleDateString('en-US', { weekday: 'narrow' })}</div>
                      <div>{dt.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const statusMeta = SUB_STATUS_STYLES[r.status] || SUB_STATUS_STYLES.not_started;
                return (
                  <tr key={r.member_id} data-testid={`heatmap-row-${r.member_id}`}>
                    <td className="sticky left-0 bg-card pr-3 py-0.5 z-10">
                      <div className="text-xs font-medium whitespace-nowrap">{r.name}</div>
                      <div className="text-[9px] text-muted-foreground">{statusMeta.label}</div>
                    </td>
                    {data.dates.map((d) => {
                      const cell = r.cells[d];
                      const state = cellState(cell);
                      const s = STATE_STYLES[state];
                      return (
                        <td key={d} className="p-0.5">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="w-full h-7 min-w-[26px] rounded-sm border hover:ring-2 hover:ring-primary/60 transition-shadow"
                                style={{ background: s.bg, borderColor: s.border }}
                                data-testid={`heatmap-cell-${r.member_id}-${d}`}
                              />
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-background" data-testid="heatmap-cell-popover">
                              <div className="space-y-2">
                                <div>
                                  <div className="font-semibold text-sm">{r.name}</div>
                                  <div className="text-xs text-muted-foreground">{fmtShortDate(d)} · <Badge variant={statusMeta.variant} className="text-[9px] py-0">{statusMeta.label}</Badge></div>
                                </div>
                                {cell.unavailable_all_day ? (
                                  <div className="text-xs font-semibold text-[#912F2F]">Not available for the entire day</div>
                                ) : (
                                  <div className="space-y-1">
                                    {SHIFT_META.map((sm) => {
                                      const st = cell.shifts?.[sm.key] || 'no_response';
                                      const sty = SHIFT_STATUS_STYLES[st] || SHIFT_STATUS_STYLES.no_response;
                                      return (
                                        <div key={sm.key} className="flex items-center justify-between text-xs">
                                          <span>{sm.label} <span className="text-muted-foreground">({sm.code})</span></span>
                                          <span className="font-semibold px-1.5 py-0.5 rounded text-[10px]" style={{ background: sty.bg, color: sty.text }}>
                                            {st === 'no_response' ? 'No Response' : st.charAt(0).toUpperCase() + st.slice(1)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {cell.restriction && <div className="text-xs italic text-[#8A6210]">Restriction: {cell.restriction}</div>}
                                {cell.note && <div className="text-xs text-muted-foreground">Note: {cell.note}</div>}
                                {r.general_notes && <div className="text-[10px] text-muted-foreground border-t pt-1.5">Period notes: {r.general_notes}</div>}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">No staff match the current filters</div>}
        </Card>
      )}
    </div>
  );
}
